import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon, 
  ClockIcon, 
  PhotoIcon, 
  ArrowRightIcon, 
  ShieldCheckIcon, 
  CalendarDaysIcon, 
  InformationCircleIcon,
  HeartIcon,
  ChatBubbleLeftRightIcon,
  ExclamationCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import api from '../../api/axios';
import supabase from '../../api/supabase';
import { toast } from 'react-hot-toast';

// Phase descriptions for better user understanding
const PHASE_DESCRIPTIONS = {
  'Site Cleaning & Layout': 'Preparing your land and marking the foundation layout',
  'Excavation': 'Digging the foundation trenches for your home',
  'PCC & Footings': 'Laying the concrete base for strong foundation',
  'Foundation': 'Building the structural foundation of your home',
  'Plinth Beam': 'Creating the load-bearing beam above foundation',
  'Ground Floor Slab': 'Pouring the concrete floor for ground level',
  'Brickwork (Ground Floor)': 'Building the walls of your ground floor',
  'Lintel & Staircase Casting (Ground)': 'Creating door frames and staircase structure',
  'Roof Waterproofing (Ground)': 'Protecting your roof from water damage',
  'Electrical & Plumbing (Concealed)': 'Installing hidden electrical wires and water pipes',
  'Plastering': 'Applying smooth finish to your walls',
  'Doors & Windows Installation': 'Fitting doors and windows in your home',
  'Flooring': 'Laying beautiful tiles and flooring materials',
  'Painting': 'Adding colors and protective coatings to walls',
  'Fixtures': 'Installing lights, switches, and bathroom fittings',
  'Final Handover & Cleaning': 'Final inspection and cleaning before handover',
  'First Floor Slab': 'Pouring the concrete floor for first level',
  'Brickwork (First Floor)': 'Building the walls of your first floor',
  'Lintel & Staircase Casting (First Floor)': 'Creating door frames and staircase for first floor',
  'Roof Waterproofing (First Floor)': 'Protecting your first floor roof',
  'Second Floor Slab': 'Pouring the concrete floor for second level',
  'Brickwork (Second Floor)': 'Building the walls of your second floor',
  'Lintel & Staircase Casting (Second Floor)': 'Creating door frames and staircase for second floor',
  'Roof Waterproofing (Second Floor)': 'Protecting your second floor roof'
};

// Helper function to get phase icon
function getPhaseIcon(status) {
  switch (status) {
    case 'Completed':
      return <CheckCircleIcon className="w-6 h-6 text-green-500" />;
    case 'In Progress':
      return <ClockIcon className="w-6 h-6 text-blue-500" />;
    case 'Not Started':
      return <ClockIcon className="w-6 h-6 text-gray-400" />;
    default:
      return <ClockIcon className="w-6 h-6 text-gray-400" />;
  }
}

// Helper function to format dates
function formatDate(dateString) {
  if (!dateString) return 'TBD';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric' 
  });
}

// Helper function to calculate days remaining
function calculateDaysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const today = new Date();
  const diffTime = end.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0;
}

// Add at the top of the file (after imports)
const FONT_STACK = 'font-sans'; // Use Tailwind's font-sans (Inter, system-ui, etc.)

const PHASE_ICONS = {
  'Site Cleaning & Layout': '🚜',
  Excavation: '⛏️',
  'PCC & Footings': '🦶',
  Foundation: '🏗️',
  'Plinth Beam': '🧱',
  'Ground Floor Slab': '🏠',
  'Brickwork (Ground Floor)': '🧱',
  'Lintel & Staircase Casting (Ground)': '🪜',
  'Roof Waterproofing (Ground)': '💧',
  'Electrical & Plumbing (Concealed)': '🔌',
  Plastering: '🖌️',
  'Doors & Windows Installation': '🚪',
  Flooring: '🪵',
  Painting: '🎨',
  Fixtures: '💡',
  'Final Handover & Cleaning': '🎁',
  'First Floor Slab': '🏢',
  'Brickwork (First Floor)': '🧱',
  'Lintel & Staircase Casting (First Floor)': '🪜',
  'Roof Waterproofing (First Floor)': '💧',
  'Second Floor Slab': '🏢',
  'Brickwork (Second Floor)': '🧱',
  'Lintel & Staircase Casting (Second Floor)': '🪜',
  'Roof Waterproofing (Second Floor)': '💧',
};

function AnimatedProgressBar({ percent }) {
  return (
    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
      <div
        className="bg-gradient-to-r from-blue-500 to-green-400 h-3 rounded-full transition-all duration-700"
        style={{ width: `${percent}%` }}
      ></div>
    </div>
  );
}

function HeaderCard({ project, stats, currentPhase }) {
  return (
    <div className={`relative rounded-2xl shadow-lg overflow-hidden mb-8 ${FONT_STACK} bg-gradient-to-br from-blue-100 via-white to-green-100`} style={{ minHeight: 180 }}>
      {/* No image, just a beautiful gradient background */}
      <div className="relative z-10 flex flex-col md:flex-row items-center md:items-end justify-between px-8 py-8 gap-6">
        <div className="flex-1">
          <div className="text-2xl md:text-3xl font-extrabold text-blue-900 drop-shadow mb-1">{project?.name || 'Your Site'}</div>
          <div className="text-base md:text-lg font-medium text-blue-800/90 mb-2">We're building your dream home.</div>
          <div className="flex items-center gap-3 mb-2">
            <span className="inline-block px-3 py-1 rounded-full bg-white/90 text-blue-700 font-semibold text-xs shadow">
              Currently: {currentPhase?.name || '—'}
            </span>
            {currentPhase && PHASE_ICONS[currentPhase.name] && (
              <span className="text-xl">{PHASE_ICONS[currentPhase.name]}</span>
            )}
          </div>
          <div className="w-full max-w-xs">
            <AnimatedProgressBar percent={stats?.progress || 0} />
            <div className="flex justify-between text-xs text-blue-900/80 mt-1">
              <span>{stats?.progress || 0}% Complete</span>
              <span>{stats?.completedPhases || 0} / {stats?.totalPhases || 0} phases</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Hero Section Component
function HeroSection({ project, latestPhoto, stats }) {
  if (!project) return null;

  const getFriendlyMessage = () => {
    if (!stats) return "Welcome to your construction journey!";
    
    if (stats.progress >= 80) {
      return "Your dream home is almost ready! 🏠✨";
    } else if (stats.progress >= 50) {
      return "Great progress! Your home is taking shape beautifully! 🏗️";
    } else if (stats.progress >= 25) {
      return "Your foundation is strong! Building your dream home step by step! 🧱";
    } else {
      return "Your construction journey has begun! We're building your dream home! 🏠";
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4 mb-4 ${FONT_STACK}`}>
      <div className="flex flex-col lg:flex-row items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-48 h-36 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center relative">
            {latestPhoto ? (
              <img 
                src={latestPhoto.file_url} 
                alt="Latest construction progress" 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center text-gray-400">
                <PhotoIcon className="w-10 h-10 mx-auto mb-1" />
                <p className="text-xs font-medium">Photo coming soon</p>
              </div>
            )}
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="flex-1 text-left">
          <h1 className="text-xl font-bold text-gray-800 mb-1 leading-tight tracking-tight">
            {project.name}
          </h1>
          
          <p className="text-sm text-gray-600 mb-2 font-normal">
            {getFriendlyMessage()}
          </p>

          {/* Progress Bar */}
          {stats && (
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs font-medium text-gray-700">Progress</span>
                <span className="text-xs font-bold text-blue-600">{stats.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded h-2">
                <div 
                  className="bg-blue-500 h-2 rounded transition-all duration-500"
                  style={{ width: `${stats.progress}%` }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{stats.completedPhases} of {stats.totalPhases} phases</span>
                {stats.daysLeft > 0 && <span>{stats.daysLeft} days left</span>}
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="flex gap-4 mt-2">
            <div className="text-base font-semibold text-blue-700">{stats?.completedPhases || 0}</div>
            <div className="text-base font-semibold text-green-700">₹{(stats?.totalSpent || 0).toLocaleString('en-IN')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// What's Next Component
function WhatsNext({ phases, stats }) {
  if (!phases || phases.length === 0) return null;

  const currentPhase = phases.find(p => p.status === 'In Progress');
  const nextPhase = phases.find(p => p.status === 'Not Started');
  const activePhase = currentPhase || nextPhase;

  if (!activePhase) return null;

  const daysRemaining = calculateDaysRemaining(activePhase.end_date);
  const progress = activePhase.progress || 0;

  return (
    <div className={`bg-gray-50 rounded-lg border border-gray-100 px-6 py-4 mb-4 ${FONT_STACK}`}>
      <div className="flex items-center gap-2 mb-2">
        <ArrowRightIcon className="w-5 h-5 text-orange-500" />
        <h2 className="text-base font-semibold text-gray-800">What's Happening Now</h2>
      </div>
      <div className="flex flex-col md:flex-row items-start gap-4">
        <div className="flex-1">
          <h3 className="text-base font-bold text-gray-800 mb-1">{activePhase.name}</h3>
          <p className="text-xs text-gray-600 mb-2 font-normal">{PHASE_DESCRIPTIONS[activePhase.name] || 'Construction work in progress'}</p>
          {currentPhase && (
            <div className="mb-2">
              <div className="flex justify-between items-center mb-1">
                <span className="text-xs text-gray-600">Phase Progress</span>
                <span className="text-xs font-semibold text-blue-600">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded h-1">
                <div 
                  className="bg-blue-500 h-1 rounded transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}
          <div className="flex gap-4 text-xs text-gray-500">
            {activePhase.start_date && <span>Start: <span className="font-medium text-gray-700">{formatDate(activePhase.start_date)}</span></span>}
            {activePhase.end_date && <span>End: <span className="font-medium text-gray-700">{formatDate(activePhase.end_date)}</span></span>}
            {daysRemaining !== null && <span className="text-blue-700 font-semibold">{daysRemaining} days left</span>}
          </div>
        </div>
        <div className="flex-shrink-0">
          <div className="p-2 bg-white rounded shadow-sm">{getPhaseIcon(activePhase.status)}</div>
        </div>
      </div>
    </div>
  );
}

// Timeline Component
function Timeline({ phases }) {
  if (!phases || phases.length === 0) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-gradient-to-r from-green-500 to-emerald-500';
      case 'In Progress':
        return 'bg-gradient-to-r from-blue-500 to-indigo-500';
      case 'Not Started':
        return 'bg-gray-300';
      default:
        return 'bg-gray-300';
    }
  };

  const getStatusTextColor = (status) => {
    switch (status) {
      case 'Completed':
        return 'text-green-700';
      case 'In Progress':
        return 'text-blue-700';
      case 'Not Started':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };

  const getPhaseIcon = (status) => {
    switch (status) {
      case 'Completed':
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-lg">
            <CheckCircleIcon className="w-5 h-5 text-white" />
          </div>
        );
      case 'In Progress':
        return (
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg animate-pulse">
            <ClockIcon className="w-5 h-5 text-white" />
          </div>
        );
      case 'Not Started':
        return (
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            <ClockIcon className="w-5 h-5 text-gray-400" />
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
            <ClockIcon className="w-5 h-5 text-gray-400" />
          </div>
        );
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4 mb-4 ${FONT_STACK}`}>
      <div className="flex items-center gap-2 mb-2">
        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
        <h2 className="text-base font-semibold text-gray-800">Construction Journey</h2>
      </div>
      
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-3 top-6 bottom-6 w-0.5 bg-gradient-to-b from-blue-200 via-gray-200 to-green-200"></div>
        
        <div className="flex flex-col gap-2">
          {phases.map((phase, idx) => (
            <div key={phase.name} className="relative flex items-start gap-3 group py-2">
              <div className="flex-shrink-0 relative z-10 mt-1">
                <div className={`w-4 h-4 rounded-full border-2 ${phase.status === 'Completed' ? 'bg-green-500 border-green-600' : phase.status === 'In Progress' ? 'bg-blue-500 border-blue-600 animate-pulse' : 'bg-gray-200 border-gray-300'}`}></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-sm font-semibold text-gray-800 truncate">{phase.name}</span>
                  <span className={`px-2 py-0.5 text-xs rounded ${phase.status === 'Completed' ? 'bg-green-100 text-green-700' : phase.status === 'In Progress' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{phase.status}</span>
                </div>
                <div className="text-xs text-gray-500 truncate">{PHASE_DESCRIPTIONS[phase.name] || 'Construction phase'}</div>
                {/* Show notes/comments if present */}
                {phase.notes && (
                  <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 my-1 font-medium">
                    <InformationCircleIcon className="w-4 h-4 inline-block mr-1 align-text-bottom" />
                    {phase.notes}
                  </div>
                )}
                <div className="flex gap-3 text-xs text-gray-400 mt-0.5">
                  {phase.start_date && <span>Start: <span className="text-gray-700 font-medium">{formatDate(phase.start_date)}</span></span>}
                  {phase.end_date && <span>End: <span className="text-gray-700 font-medium">{formatDate(phase.end_date)}</span></span>}
                  {phase.progress > 0 && <span>{phase.progress}%</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-gray-100 flex gap-6 text-xs text-gray-500 font-medium">
        <span>{phases.filter(p => p.status === 'Completed').length} completed</span>
        <span>{phases.filter(p => p.status === 'In Progress').length} active</span>
        <span>{phases.filter(p => p.status === 'Not Started').length} remaining</span>
      </div>
    </div>
  );
}

// Site Gallery Component
function SiteGallery({ photos }) {
  // Group photos by phase
  const grouped = {};
  (photos || []).forEach(photo => {
    const phase = photo.phase || 'Other';
    if (!grouped[phase]) grouped[phase] = [];
    grouped[phase].push(photo);
  });
  const phaseNames = Object.keys(grouped);

  // Lightbox state
  const [lightbox, setLightbox] = useState({ open: false, phase: null, idx: 0 });
  const openLightbox = (phase, idx) => setLightbox({ open: true, phase, idx });
  const closeLightbox = () => setLightbox({ open: false, phase: null, idx: 0 });
  const nextImg = () => setLightbox(l => ({ ...l, idx: (l.idx + 1) % grouped[l.phase].length }));
  const prevImg = () => setLightbox(l => ({ ...l, idx: (l.idx - 1 + grouped[l.phase].length) % grouped[l.phase].length }));

  if (!photos || photos.length === 0) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-8 mb-4 ${FONT_STACK} text-center`}>
        <PhotoIcon className="w-12 h-12 mx-auto mb-2 text-blue-200" />
        <h2 className="text-lg font-semibold text-gray-700 mb-1">No Site Photos Yet</h2>
        <p className="text-gray-400">Photos of your construction progress will appear here as soon as our team uploads them.</p>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-8 mb-4 ${FONT_STACK}`}> 
      <div className="flex items-center gap-2 mb-6">
        <PhotoIcon className="w-6 h-6 text-purple-500" />
        <h2 className="text-xl font-bold text-gray-800">Site Gallery</h2>
      </div>
      <div className="space-y-10">
        {phaseNames.map(phase => (
          <div key={phase} className="mb-2">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{PHASE_ICONS[phase] || '🏗️'}</span>
              <span className="font-semibold text-gray-700 text-lg">{phase}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {grouped[phase].map((photo, idx) => (
                <div key={photo.file_url} className="relative group cursor-pointer animate-fade-in" style={{animationDelay: `${0.05*idx}s`, animationFillMode:'forwards'}}>
                  <div className="aspect-square rounded-xl overflow-hidden shadow-md bg-gray-100 group-hover:shadow-xl transition-shadow duration-300">
                    <img 
                      src={photo.file_url} 
                      alt={photo.caption || `Site photo ${idx + 1}`} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      onClick={() => openLightbox(phase, idx)}
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 rounded-b-xl opacity-90 flex flex-col">
                    <span>{photo.caption || 'Site photo'}</span>
                    {photo.uploaded_at && <span className="text-[10px] text-gray-200">{new Date(photo.uploaded_at).toLocaleDateString()}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {/* Lightbox Modal */}
      {lightbox.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 animate-fade-in">
          <div className="relative bg-white rounded-2xl shadow-2xl p-4 max-w-lg w-full flex flex-col items-center">
            <button onClick={closeLightbox} className="absolute top-2 right-2 text-gray-500 hover:text-red-500 text-xl font-bold">×</button>
            <img src={grouped[lightbox.phase][lightbox.idx].file_url} alt="Full size" className="rounded-xl max-h-[60vh] object-contain mb-2" />
            <div className="flex items-center gap-2 mb-2">
              <span className="text-2xl">{PHASE_ICONS[lightbox.phase] || '🏗️'}</span>
              <span className="font-semibold text-gray-700 text-base">{lightbox.phase}</span>
            </div>
            <div className="text-xs text-gray-600 mb-2">{grouped[lightbox.phase][lightbox.idx].caption}</div>
            {grouped[lightbox.phase][lightbox.idx].uploaded_at && <div className="text-[10px] text-gray-400 mb-2">{new Date(grouped[lightbox.phase][lightbox.idx].uploaded_at).toLocaleDateString()}</div>}
            <div className="flex gap-4 mt-2">
              <button onClick={prevImg} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">Prev</button>
              <button onClick={nextImg} className="px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700">Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Help Section Component
function HelpSection() {
  return (
    <div className={`bg-gray-50 rounded-lg border border-gray-100 px-6 py-4 mb-4 ${FONT_STACK}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl shadow-lg">
          <ChatBubbleLeftRightIcon className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-base font-semibold text-gray-800">Need Help?</h2>
          <p className="text-xs text-gray-600 mb-2">Have questions about your construction progress? Our team is here to help you every step of the way!</p>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button className="flex items-center gap-1 px-4 py-2 bg-green-500 text-white rounded font-semibold text-xs hover:bg-green-600 transition">
          <ChatBubbleLeftRightIcon className="w-4 h-4" />
          Contact Support
        </button>
        <button className="flex items-center gap-1 px-4 py-2 bg-white text-gray-700 rounded border border-gray-200 font-semibold text-xs hover:bg-gray-100 transition">
          <InformationCircleIcon className="w-4 h-4" />
          View FAQ
        </button>
      </div>
    </div>
  );
}

// Loading Component
function LoadingState() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-white py-8 px-2 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="animate-pulse">
          <div className="bg-gray-200 rounded-3xl h-80 mb-8"></div>
          <div className="bg-gray-200 rounded-2xl h-32 mb-8"></div>
          <div className="bg-gray-200 rounded-2xl h-64 mb-8"></div>
          <div className="bg-gray-200 rounded-2xl h-48 mb-8"></div>
        </div>
      </div>
    </div>
  );
}

// Error Component
function ErrorState({ error, onRetry }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-white py-8 px-2 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-6">
            {error || 'Unable to load your construction data. Please try again.'}
          </p>
          <button 
            onClick={onRetry}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors mx-auto"
          >
            <ArrowPathIcon className="w-5 h-5" />
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsHappeningNow({ currentPhase, phases }) {
  if (!currentPhase) return null;
  // Calculate phase index and days
  const idx = phases.findIndex(p => p.name === currentPhase.name);
  const totalPhases = phases.length;
  const today = new Date();
  const start = currentPhase.start_date ? new Date(currentPhase.start_date) : null;
  const end = currentPhase.end_date ? new Date(currentPhase.end_date) : null;
  const daysInPhase = start && end ? Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1) : null;
  const daysElapsed = start ? Math.max(1, Math.round((today - start) / (1000 * 60 * 60 * 24)) + 1) : null;
  const daysLeft = end ? Math.max(0, Math.round((end - today) / (1000 * 60 * 60 * 24))) : null;
  const nextPhase = phases[idx + 1];
  return (
    <div className="relative rounded-2xl shadow-md bg-white px-6 py-5 mb-8 flex flex-col md:flex-row items-center gap-4 border border-blue-50 animate-fade-in">
      <div className="flex items-center gap-4 flex-1 min-w-0">
        <div className="text-3xl md:text-4xl">{PHASE_ICONS[currentPhase.name] || '🏗️'}</div>
        <div className="flex-1 min-w-0">
          <div className="text-lg md:text-xl font-bold text-blue-900 mb-1 truncate">{currentPhase.name}</div>
          <div className="text-sm text-gray-500 mb-1">{currentPhase.status === 'In Progress' ? 'Work is actively happening in this phase.' : 'This phase is about to begin.'}</div>
          {/* Show notes/comments if present */}
          {currentPhase.notes && (
            <div className="text-xs text-blue-700 bg-blue-50 rounded px-2 py-1 mb-1 font-medium">
              <InformationCircleIcon className="w-4 h-4 inline-block mr-1 align-text-bottom" />
              {currentPhase.notes}
            </div>
          )}
          <div className="flex gap-4 text-xs text-gray-500 mb-1">
            {start && <span>Started: <span className="font-medium text-gray-700">{start.toLocaleDateString()}</span></span>}
            {end && <span>Ends: <span className="font-medium text-gray-700">{end.toLocaleDateString()}</span></span>}
          </div>
          {daysInPhase && daysElapsed && (
            <div className="flex items-center gap-2 text-xs text-blue-700 font-semibold">
              <ClockIcon className="w-4 h-4 inline-block" />
              Day {Math.min(daysElapsed, daysInPhase)} of {daysInPhase}
              {daysLeft !== null && <span className="ml-2">({daysLeft} days left)</span>}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2 min-w-[160px]">
        {nextPhase && (
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 rounded-full px-3 py-1 font-medium shadow-sm">
            <ArrowRightIcon className="w-4 h-4 text-blue-400" />
            Up next: <span className="font-semibold text-blue-700 ml-1">{nextPhase.name}</span>
            {PHASE_ICONS[nextPhase.name] && <span className="ml-1 text-lg">{PHASE_ICONS[nextPhase.name]}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyConstructionPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch user's projects first
      const projectsResponse = await api.get('/projects');
      const userProjects = projectsResponse.data.data;
      
      if (!userProjects || userProjects.length === 0) {
        setError('No active project found. Please contact support if you believe this is an error.');
        setLoading(false);
        return;
      }

      const currentProject = userProjects[0]; // Get the most recent project
      setProject(currentProject);

      // Fetch all data in parallel
      const [phasesResponse, photosResponse, statsResponse] = await Promise.all([
        api.get('/construction/phases'),
        api.get('/construction/photos'),
        api.get('/dashboard/stats')
      ]);

      setPhases(phasesResponse.data || []);
      setPhotos(photosResponse.data || []);
      setStats(statsResponse.data);

    } catch (err) {
      console.error('Error fetching construction data:', err);
      setError(err.response?.data?.message || 'Failed to load construction data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!project?.id) return;
    // Set up real-time subscriptions for customer view
    const projectSubscription = supabase
      .channel('customer-project-changes')
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'projects' },
        (payload) => {
          if (payload.new.id === project.id) {
            setProject(payload.new);
            toast.success('Your project has been updated!');
            // Re-fetch phases to ensure latest data
            api.get('/construction/phases').then(res => setPhases(res.data || []));
          }
        }
      )
      .subscribe();

    const photosSubscription = supabase
      .channel('customer-photos-changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'site_photos' },
        (payload) => {
          if (payload.new.project_id === project.id) {
            setPhotos(prev => [payload.new, ...prev]);
            toast.success('New construction photo uploaded!');
          }
        }
      )
      .subscribe();

    return () => {
      projectSubscription.unsubscribe();
      photosSubscription.unsubscribe();
    };
  }, [project?.id]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchData} />;
  }

  const latestPhoto = photos.length > 0 ? photos[0] : null;

  // Find current phase
  const currentPhase = phases.find(p => p.status === 'In Progress') || phases.find(p => p.status === 'Not Started') || phases[0];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-white py-8 px-2 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <HeaderCard project={project} stats={stats} currentPhase={currentPhase} />
        <WhatsHappeningNow currentPhase={currentPhase} phases={phases} />
        <Timeline phases={phases} />
        <SiteGallery photos={photos} />
        <HelpSection />
      </div>
    </div>
  );
}
